import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DeleteConfirmationModal from './delete-confirmation-modal';
import { RIGHT_PANELS } from 'constants/feature/panel-constants';
import { fetchTheListOfNotes } from 'store/actions/notes-panel-actions';
import { fetchStatementSummarySections } from 'store/actions/statement-summary/sections-summary-actions';
import { connect } from 'react-redux';
import { deleteRevisionsSectionRequest } from 'api/sections-api';
import { OK } from 'http-status-codes';
import { immediateScrollToSectionWhenLoaded } from 'utils/scrolling-utils';
import {
  fetchAllSectionRenderingData,
  fetchSectionIdListStatementContent,
  updateSectionDetailsStore,
} from 'store/actions/statement-content-actions';
import {
  getContentSectionMapRequest,
  getContentSectionMapOCRRequest,
} from 'store/actions/section-cache-actions';
import { setTOCRefresh } from 'store/actions/TOC-actions';
import { fetchStatementSummaryElements } from 'store/actions/statement-summary/elements-summary-actions';
import {
  sectionTreeListLoading,
  fetchSectionTreeAction,
} from 'store/actions/section-tree-list-actions';
import SectionContentModel from 'models/api/section-content-api-model';

const DeleteSectionModal = ({
  onClose,
  onSuccess,
  node,
  socketHasBeenDisconnected,
  fetchTheListOfNotes,
  fetchStatementSummarySections,
  fetchSectionIdListStatementContent,
  getContentSectionMapRequest,
  getContentSectionMapOCRRequest,
  fetchSectionTreeAction,
  setTOCRefresh,
  fetchStatementSummaryElements,
  fetchAllSectionRenderingData,
  updateSectionDetailsStore,
  sectionTreeListLoading,
  revision,
  sectionReviewList,
  panels,
  isOCR,
  contentSectionMap,
}) => {
  const checkIfParentBookmark = useMemo(() => {
    // Only check for parent bookmarks when in OCR mode
    if (!isOCR) {
      return false;
    }

    const contentSectionMapFlattenArray = Object.values(contentSectionMap.map)
      ?.flatMap((sectionMap) => sectionMap)
      ?.filter((section) => !section?.deleted);

    const existingChildren = contentSectionMapFlattenArray.some(
      (section) => section.parentId === node.id,
    );
    return existingChildren;
  }, [contentSectionMap, node.id, isOCR]);

  const onSave = async (node, name, onSuccess) => {
    const section = { ...node };
    const sectionId = !isOCR ? section.id : node.id;

    let isSectionReviewed = 0;
    if (sectionReviewList && sectionReviewList.sectionReviews) {
      for (let i = 0; i < sectionReviewList.sectionReviews.length; i++) {
        if (
          sectionReviewList.sectionReviews[i][
            isOCR ? 'bookmarkId' : 'sectionId'
          ] === sectionId
        ) {
          isSectionReviewed = 1;
          break;
        }
      }
    }
    onClose();

    sectionTreeListLoading();
    await deleteRevisionsSectionRequest(
      section.revisionId,
      sectionId,
      name,
      isSectionReviewed,
      isOCR,
    )
      .then((resp) => {
        if (resp && resp.status === OK) {
          onSuccess();
          !isOCR && fetchSectionIdListStatementContent(section.revisionId);
          isOCR
            ? getContentSectionMapOCRRequest({
                revisionId: section.revisionId,
                isOCR,
              })
            : getContentSectionMapRequest({
                revisionId: section.revisionId,
              });
          setTOCRefresh(true);
          if (socketHasBeenDisconnected) {
            fetchStatementSummaryElements({
              revisionId: section.revisionId,
            });
          }
          isOCR &&
            fetchSectionTreeAction({
              revisionId: section.revisionId,
              isOCR,
            });
          if (!isOCR && resp.data && resp.data.id) {
            fetchAllSectionRenderingData({
              sectionId: resp.data.id,
            });
            immediateScrollToSectionWhenLoaded({ sectionId: resp.data.id });
          }
          if (panels.right === RIGHT_PANELS.SECTION) {
            updateSectionDetailsStore({ sectionId, isDelete: true });
          }
        }
      })
      .catch((error) => {
        // Handle any errors that occur during the delete request
        console.error('Delete section request failed:', error);
      });
  };

  const onConfirmDelete = async () => {
    await onSave(node, node.name, onSuccess);
    if (panels.right === RIGHT_PANELS.NOTES) {
      fetchTheListOfNotes({ revisionId: revision.id });
    }
    if (socketHasBeenDisconnected) {
      fetchStatementSummarySections({ revisionId: revision.id });
    }
  };

  return (
    <>
      <DeleteConfirmationModal
        onClose={onClose}
        onDelete={onConfirmDelete}
        deletingParentBookmark={isOCR && checkIfParentBookmark}
      />
    </>
  );
};

const mapStateToProps = ({
  data: {
    selectedStatement: { isOCR },
    revision,
    sectionPanel: { sectionReviewList },
  },
  ui: {
    statementPage: { panels },
  },
  sockets: {
    statementSocket: { socketHasBeenDisconnected },
  },
}) => ({
  revision,
  panels,
  socketHasBeenDisconnected,
  sectionReviewList,
  isOCR,
});

const mapDispatchToProps = {
  fetchTheListOfNotes,
  fetchStatementSummarySections,
  fetchSectionIdListStatementContent,
  getContentSectionMapRequest,
  setTOCRefresh,
  fetchStatementSummaryElements,
  fetchAllSectionRenderingData,
  updateSectionDetailsStore,
  sectionTreeListLoading,
  getContentSectionMapOCRRequest,
  fetchSectionTreeAction,
};

DeleteSectionModal.propTypes = {
  /** Section the segment is responsible for fetching and rendering */
  node: PropTypes.instanceOf(SectionContentModel),
  /** Modal panel onClose fucntion  */
  onClose: PropTypes.func.isRequired,
  /** redux action to refresh table of content panel  */
  setTOCRefresh: PropTypes.func.isRequired,
  /** redux action to retrieve section details  */
  fetchAllSectionRenderingData: PropTypes.func.isRequired,
  /** redux action to retrieve statement summary elements  */
  fetchStatementSummaryElements: PropTypes.func.isRequired,
  /** redux action to retrieve content section map  */
  getContentSectionMapRequest: PropTypes.func.isRequired,
  /** redux action to retrieve section id list  */
  fetchSectionIdListStatementContent: PropTypes.func.isRequired,
  /** redux action to load and scroll to selected section in content body  */
  immediateScrollToSectionWhenLoaded: PropTypes.func.isRequired,
  /** Action fired to update or delete the selection section details in store */
  updateSectionDetailsStore: PropTypes.func.isRequired,
  /*boolean value that indicates if the websocket connection has failed */
  socketHasBeenDisconnected: PropTypes.bool.isRequired,
  /** method to fetch the notes list  */
  fetchTheListOfNotes: PropTypes.func.isRequired,
  /** action that updates the number of sections in the header */
  fetchStatementSummarySections: PropTypes.func.isRequired,
  /**action to set loading state for section tree list */
  sectionTreeListLoading: PropTypes.func.isRequired,
  /** check if modal is open for ocr or legacy */
  isOCR: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteSectionModal);
